import { memo, useEffect, useRef } from "react";
import { useLocalization } from "@fluent/react";
import { formatNumber } from "@narmi/design_system";
import { format } from "date-fns";
import cc from "classcat";
import type { ChartDatum } from "../../Charts/types";
import styles from "./NetWorthMonthlyList.module.scss";

type NetWorthMonthlyListProps = {
  chartData: ChartDatum[];
  /** The hovered data point, or null to default to highlighting the most recent month. */
  activeDatum: ChartDatum | null;
  /**
   * Called when a month row is clicked, to show that month's gains/losses.
   * The earliest month in chartData has no prior month to diff against, so
   * its row isn't clickable when this is provided.
   */
  onSelectMonth?: (datum: ChartDatum) => void;
};

const NetWorthMonthlyList = ({
  chartData,
  activeDatum,
  onSelectMonth
}: NetWorthMonthlyListProps) => {
  const { l10n } = useLocalization();
  const rowRefs = useRef(new Map<number, HTMLElement>());

  const highlightedTime =
    chartData.length === 0
      ? null
      : (activeDatum ?? chartData[chartData.length - 1]).x.getTime();

  // Longer windows (9-month/annual) show more rows than fit without
  // scrolling, so the highlighted row can scroll out of view as the user
  // hovers across the chart - bring it back into view when that happens.
  useEffect(() => {
    if (!activeDatum) return;
    rowRefs.current.get(activeDatum.x.getTime())?.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  }, [activeDatum]);

  if (chartData.length === 0) return null;

  const earliestTime = chartData[0].x.getTime();
  // chartData is chronological (oldest first); show most recent first.
  const orderedData = [...chartData].reverse();

  return (
    <div className={styles.container}>
      <h4 className={styles.title}>
        {l10n.getString("heading-net-worth-monthly-list")}
      </h4>
      <div className={styles.list} data-testid="net-worth-monthly-list">
        {orderedData.map((datum) => {
          const rowClassName = cc([
            styles.row,
            datum.x.getTime() === highlightedTime && styles.rowActive
          ]);
          const rowContent = (
            <>
              <span>{format(datum.x, "MMMM yyyy")}</span>
              <span>{formatNumber(datum.y, "currency")}</span>
            </>
          );
          const isClickable =
            !!onSelectMonth && datum.x.getTime() !== earliestTime;
          const setRowRef = (el: HTMLElement | null) => {
            if (el) rowRefs.current.set(datum.x.getTime(), el);
            else rowRefs.current.delete(datum.x.getTime());
          };

          if (!isClickable) {
            return (
              <div
                key={datum.x.getTime()}
                ref={setRowRef}
                data-testid="net-worth-monthly-list-row"
                className={rowClassName}
              >
                {rowContent}
              </div>
            );
          }

          return (
            <button
              key={datum.x.getTime()}
              ref={setRowRef}
              type="button"
              data-testid="net-worth-monthly-list-row"
              className={cc([
                rowClassName,
                styles.rowClickable,
                "button--reset"
              ])}
              onClick={() => onSelectMonth?.(datum)}
            >
              {rowContent}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default memo(NetWorthMonthlyList);
